// src/pages/WelcomePage.tsx — стиль мобильного приложения (design/)
import React from 'react';
import { Button, Typography, Card, Row, Col, Space } from 'antd';
import {
  CalendarOutlined,
  FileTextOutlined,
  MessageOutlined,
  LineChartOutlined,
  AppleOutlined,
  LoginOutlined,
  UserAddOutlined,
  MailOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import './WelcomePage.css';

const { Title, Paragraph, Text } = Typography;

const primary = '#7CB342';
const text = '#263238';
const muted = '#616161';
const APP_STORE_URL = 'https://apps.apple.com/app/id6764552008';

export const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const narrow = useIsMobile();

  const features = [
    {
      icon: <CalendarOutlined style={{ fontSize: 28, color: primary }} />,
      bg: '#E8F5E9',
      title: 'Дневники беременности',
      description:
        'Глюкоза, вес, симптомы, посещения и анализы — всё в одном месте, как в приложении.',
    },
    {
      icon: <LineChartOutlined style={{ fontSize: 28, color: '#42A5F5' }} />,
      bg: '#E3F2FD',
      title: 'Графики и динамика',
      description: 'Наглядно видите динамику показателей здоровья.',
    },
    {
      icon: <MessageOutlined style={{ fontSize: 28, color: '#AB47BC' }} />,
      bg: '#F3E5F5',
      title: 'Чат с врачом',
      description: 'Безопасные сообщения и консультации с лечащим врачом.',
    },
    {
      icon: <FileTextOutlined style={{ fontSize: 28, color: '#FF7043' }} />,
      bg: '#FBE9E7',
      title: 'История наблюдений',
      description: 'Вся информация о беременности доступна в любое время.',
    },
  ];

  const benefits = [
    'Спокойный интерфейс в одной стилистике с приложением',
    'Безопасное хранение данных',
    'Доступ с телефона и компьютера',
    'Напоминания и геймификация',
    'Удобно и врачам, и будущим мамам',
  ];

  return (
    <div className="welcome-page">
      <header className="welcome-header">
        <div className="header-content">
          <div className="logo-section">
            <span className="logo-compass">compass</span>
            <span className="logo-mum">mum</span>
          </div>
          <div className="header-actions">
            <Button
              size="large"
              href="https://t.me/compass_mom"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                borderRadius: 14,
                borderColor: '#B0BEC5',
                color: '#0088cc',
                fontWeight: 600,
              }}
            >
              Telegram — Компасс Мам
            </Button>
            <Button
              size="large"
              onClick={() => navigate('/login')}
              style={{
                borderRadius: 14,
                borderColor: '#C8E6C9',
                color: text,
                fontWeight: 600,
              }}
              icon={<LoginOutlined />}
            >
              Войти
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<UserAddOutlined />}
              onClick={() => navigate('/register')}
              style={{
                fontWeight: 600,
                borderRadius: 14,
                height: 46,
              }}
            >
              Зарегистрироваться
            </Button>
          </div>
        </div>
      </header>

      <section className="hero-section">
        <div className="hero-content">
          <Title level={1} className="hero-title">
            Счастливого пути в мир <em>материнства!</em>
          </Title>
          <p className="hero-description">
            Рассчитаем срок и будем следить за развитием малыша — на сайте и в приложении
            Compass mum.
          </p>
          <Space
            size="middle"
            direction={narrow ? 'vertical' : 'horizontal'}
            style={{ width: narrow ? '100%' : 'auto' }}
          >
            <Button
              type="default"
              size="large"
              className="hero-cta"
              onClick={() => navigate('/register')}
              block={narrow}
            >
              Продолжить
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<AppleOutlined />}
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              block={narrow}
              style={{
                borderRadius: 14,
                height: 48,
                fontWeight: 600,
                backgroundColor: '#263238',
                borderColor: '#263238',
              }}
            >
              Скачать в App Store
            </Button>
          </Space>
        </div>
      </section>

      <section className="features-section">
        <div className="section-content">
          <Title level={2} className="section-title">
            Возможности
          </Title>
          <Row gutter={[20, 20]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card className="feature-card" hoverable styles={{ body: { padding: 22 } }}>
                  <div
                    className="feature-icon-wrap"
                    style={{ background: feature.bg }}
                  >
                    {feature.icon}
                  </div>
                  <Title level={4} style={{ color: text, marginBottom: 10, textAlign: 'center' }}>
                    {feature.title}
                  </Title>
                  <Paragraph style={{ color: muted, margin: 0, textAlign: 'center', fontSize: 14 }}>
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      <section className="benefits-section">
        <div className="section-content">
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={2} style={{ color: text, marginBottom: 20, fontWeight: 700 }}>
                Почему compass mum?
              </Title>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={index} className="benefit-item">
                    <Text style={{ fontSize: 16, color: muted }}>{benefit}</Text>
                  </li>
                ))}
              </ul>
            </Col>
            <Col xs={24} lg={10}>
              <div className="benefits-highlight">
                <Text style={{ fontSize: 18, fontWeight: 600, color: primary }}>
                  Дневник здоровья · Медали · Недели беременности
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      <section className="cta-section">
        <div className="section-content">
          <Card className="cta-card" styles={{ body: { padding: '48px 28px' } }}>
            <Title level={2} style={{ color: '#fff', marginBottom: 12, textAlign: 'center' }}>
              Готовы начать?
            </Title>
            <Paragraph
              style={{
                color: 'rgba(255,255,255,0.95)',
                fontSize: 16,
                marginBottom: 28,
                textAlign: 'center',
                maxWidth: 520,
                margin: '0 auto 28px',
              }}
            >
              Тот же аккаунт на сайте и в приложении — ведите дневники там, где удобнее.
            </Paragraph>
            <Space
              size="middle"
              direction={narrow ? 'vertical' : 'horizontal'}
              style={{ width: '100%', justifyContent: 'center', display: 'flex' }}
            >
              <Button
                size="large"
                onClick={() => navigate('/login')}
                style={{
                  background: 'rgba(255,255,255,0.95)',
                  color: text,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 14,
                  minWidth: 140,
                  width: narrow ? '100%' : 'auto',
                }}
              >
                Войти
              </Button>
              <Button
                size="large"
                onClick={() => navigate('/register')}
                style={{
                  background: '#fff',
                  color: primary,
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: 14,
                  minWidth: 180,
                  width: narrow ? '100%' : 'auto',
                }}
              >
                Зарегистрироваться
              </Button>
            </Space>
          </Card>
        </div>
      </section>

      <section className="partnership-section">
        <div className="section-content">
          <Card
            style={{
              marginBottom: 24,
              borderRadius: 24,
              border: '1px solid rgba(0, 136, 204, 0.25)',
              background: 'linear-gradient(135deg, #E3F2FD 0%, #E8F5E9 100%)',
            }}
            styles={{ body: { padding: '32px 28px', textAlign: 'center' } }}
          >
            <Title level={3} style={{ color: text, marginBottom: 12 }}>
              Компасс Мам в Telegram
            </Title>
            <Paragraph style={{ color: muted, fontSize: 16, maxWidth: 560, margin: '0 auto 20px' }}>
              Поддержка, профессиональная информация и эмоциональное сопровождение на всех этапах беременности
              и раннего материнства — подписывайтесь на канал.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              href="https://t.me/compass_mom"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: '#0088cc',
                borderColor: '#0088cc',
                borderRadius: 14,
                fontWeight: 600,
                height: 48,
              }}
            >
              Перейти в Telegram @compass_mom
            </Button>
          </Card>
          <Card className="partnership-card" styles={{ body: { padding: '44px 28px', textAlign: 'center' } }}>
            <TeamOutlined style={{ fontSize: 48, color: '#42A5F5', marginBottom: 16 }} />
            <Title level={2} style={{ color: text, marginBottom: 12 }}>
              Сотрудничество
            </Title>
            <Paragraph style={{ color: muted, fontSize: 16, maxWidth: 560, margin: '0 auto 24px' }}>
              Мы открыты для партнёрства с клиниками и врачами. Пишите на почту.
            </Paragraph>
            <MailOutlined style={{ fontSize: 22, color: primary, marginRight: 8 }} />
            <a
              href="mailto:info@compass-mom.ru"
              style={{
                fontSize: 18,
                color: primary,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              info@compass-mom.ru
            </a>
          </Card>
        </div>
      </section>

      <footer className="welcome-footer">
        <div className="footer-content">
          <Space direction="vertical" size={8}>
            <a
              href="https://t.me/compass_mom"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0088cc', fontWeight: 600 }}
            >
              Telegram: @compass_mom
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#263238', fontWeight: 600 }}
            >
              App Store — скачать приложение
            </a>
            <Text style={{ color: muted }}>© 2026 compass mum. Все права защищены.</Text>
          </Space>
        </div>
      </footer>
    </div>
  );
};
